import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
} from '@/utils/dataNormalization';
import {
  confirmFeeApproveFlow,
  confirmFeeDeleteFlow,
  closeFeeModalState,
  notifyFeeActionPending,
  resolveFeeApprovalTargetState,
  openFeeEditModalFromRow,
  openFeeModalState,
  submitFeePayFlow,
  submitFeeFlow,
} from '@/composables/fees/useFeeFlow';
import {
  fetchFeesFilterOptions,
  fetchFeesSnapshot,
  getFeesFilterOptions,
} from '@/composables/fees/useFeesList';
import { ensureValidPage } from '@/composables/shared/usePagination';
import {
  createInitialFeeSummary,
} from '@/composables/sales/saleDetail/state';
import { FEES_TEXT } from '@/texts/catalog/fees';

const FEES_FLOW_TEXT = FEES_TEXT.flow;

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeFeeSummarySection = (section, fallbackTotal = 0) => {
  if (!section || typeof section !== 'object') {
    const total = toFiniteNumber(fallbackTotal, 0);

    return {
      subtotal: total,
      transferred_taxes: 0,
      withheld_taxes: 0,
      total,
    };
  }

  const total = toFiniteNumber(section.total, toFiniteNumber(fallbackTotal, 0));

  return {
    subtotal: toFiniteNumber(section.subtotal, total),
    transferred_taxes: toFiniteNumber(
      section.transferred_taxes,
      toFiniteNumber(section.transfered_taxes, 0)
    ),
    withheld_taxes: toFiniteNumber(section.withheld_taxes, 0),
    total,
  };
};

const normalizeFeeSummaryPayload = (payload = {}) => {
  const summary = payload && typeof payload.summary === 'object' ? payload.summary : {};

  const totalFallback = toFiniteNumber(payload.total, toFiniteNumber(payload.total_expenses, 0));
  const paidFallback = toFiniteNumber(payload.paid_total, toFiniteNumber(payload.total_paid, toFiniteNumber(payload.paid, 0)));
  const pendingFallback = toFiniteNumber(payload.pending_total, toFiniteNumber(payload.pending_to_pay, toFiniteNumber(payload.pending, 0)));

  const totalSection = normalizeFeeSummarySection(
    summary.fees || summary.fee || summary.payment_plan,
    totalFallback
  );
  const paidSection = normalizeFeeSummarySection(summary.paid, paidFallback);
  const pendingSection = normalizeFeeSummarySection(summary.pending, pendingFallback);

  return {
    total: totalSection.total,
    paid_total: paidSection.total,
    pending_total: pendingSection.total,
    fees: totalSection,
    paid: paidSection,
    pending: pendingSection,
  };
};

export const saleDetailFeeMethods = {
  ensureFeePageInRange() {
    this.feeCurrentPage = ensureValidPage(this.feeCurrentPage, this.feeTotalPages);
  },
  clearFeeSearchDebounce() {
    if (!this.feeSearchDebounceTimer) {
      return;
    }

    clearTimeout(this.feeSearchDebounceTimer);
    this.feeSearchDebounceTimer = null;
  },
  scheduleFeeSearchFetch() {
    this.clearFeeSearchDebounce();

    this.feeSearchDebounceTimer = setTimeout(() => {
      this.feeSearchDebounceTimer = null;
      this.fetchSaleFees();
    }, this.feeSearchDebounceMs);
  },
  normalizeSaleFeesFilters(filters = {}) {
    const source = (filters && typeof filters === 'object') ? filters : {};

    return {
      search: String(source.search || '').trim(),
      date_start: String(source.date_start || '').trim(),
      date_end: String(source.date_end || '').trim(),
      provider: Array.isArray(source.provider) ? source.provider : [],
      collaborator: Array.isArray(source.collaborator) ? source.collaborator : [],
      expense_class: Array.isArray(source.expense_class) ? source.expense_class : [],
      category: Array.isArray(source.category) ? source.category : [],
      customer: Array.isArray(source.customer) ? source.customer : [],
      tags: Array.isArray(source.tags) ? source.tags : [],
      has_xml: Array.isArray(source.has_xml) ? source.has_xml : [],
      currency: Array.isArray(source.currency) ? source.currency : [],
      area: Array.isArray(source.area) ? source.area : [],
    };
  },
  buildSaleFeesParams() {
    const filters = this.normalizeSaleFeesFilters(this.feeFilters);

    return {
      search: String(this.feeSearchText || filters.search || '').trim(),
      date_start: filters.date_start,
      date_end: filters.date_end,
      provider: filters.provider,
      collaborator: filters.collaborator,
      expense_class: filters.expense_class,
      category: filters.category,
      customer: filters.customer,
      tags: filters.tags,
      has_xml: filters.has_xml,
      currency: filters.currency,
      area: filters.area,
      sale_id: this.saleId,
    };
  },
  async ensureSaleFeeFiltersLoaded() {
    if (this.feeFiltersLoaded) {
      return;
    }

    try {
      this.feeFilterOptions = await fetchFeesFilterOptions();
    } catch (error) {
      this.feeFilterOptions = getFeesFilterOptions();
    } finally {
      this.feeFiltersLoaded = true;
    }
  },
  async fetchSaleFees() {
    if (!this.saleId) {
      this.feeRows = [];
      this.feeSummary = createInitialFeeSummary();
      return;
    }

    await this.ensureSaleFeeFiltersLoaded();

    const params = this.buildSaleFeesParams();
    const requestKey = JSON.stringify(params);

    if (this.feeLoading && this.feeInFlightRequestKey === requestKey) {
      return;
    }

    const requestToken = ++this.feeListRequestToken;
    this.feeInFlightRequestKey = requestKey;
    this.feeLoading = true;

    try {
      const snapshot = await fetchFeesSnapshot(params);

      if (requestToken === this.feeListRequestToken) {
        const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : [];
        this.feeRows = rows;
        this.feeSummary = normalizeFeeSummaryPayload(snapshot?.metrics || {});
        this.ensureFeePageInRange();
      }
    } catch (error) {
      if (requestToken === this.feeListRequestToken) {
        this.feeRows = [];
        this.feeSummary = createInitialFeeSummary();
      }
    } finally {
      if (requestToken === this.feeListRequestToken) {
        this.feeLoading = false;
        if (this.feeInFlightRequestKey === requestKey) {
          this.feeInFlightRequestKey = '';
        }
      }
    }
  },
  handleSaleFeesSearchChange(payload) {
    const nextSearch = String(payload?.search || '').trim();

    this.feeSearchText = nextSearch;
    this.feeFilters = {
      ...this.normalizeSaleFeesFilters(this.feeFilters),
      search: nextSearch,
    };
    this.feeCurrentPage = 1;
    this.scheduleFeeSearchFetch();
  },
  handleSaleFeesFiltersChange(payload) {
    const nextFilters = this.normalizeSaleFeesFilters(payload);

    this.feeFilters = nextFilters;
    this.feeSearchText = String(nextFilters.search || '').trim();
    this.feeCurrentPage = 1;
    this.fetchSaleFees();
  },
  handleSaleFeesStatusTabSelect(tabKey) {
    const normalized = String(tabKey || '').trim();
    if (normalized === '' || normalized === this.feeActiveStatusTab) {
      return;
    }

    this.feeActiveStatusTab = normalized;
    this.feeCurrentPage = 1;
  },
  handleSaleFeesPageChange(nextPage) {
    this.feeCurrentPage = Number(nextPage || 1);
    this.ensureFeePageInRange();
  },
  handleSalePayFee(row) {
    if (this.isPayFeeSubmitting) {
      return;
    }

    this.payFeeTarget = row && typeof row === 'object' ? { ...row } : null;
    this.isPayFeeModalOpen = true;
  },
  handleSaleEditFee(row) {
    openFeeEditModalFromRow(this, row, {
      mapInitialData: ({ detail, rowData }) => {
        const mergedPayload = {
          ...rowData,
          ...detail,
        };

        if (String(mergedPayload.status || '').trim() === '') {
          mergedPayload.status = String(rowData.status || '').trim();
        }

        if (Number(mergedPayload.payment_id || 0) <= 0) {
          mergedPayload.payment_id = Number(
            detail.payment_id
            || rowData.payment_id
            || rowData.payment?.id
            || 0
          ) || 0;
        }

        if (mergedPayload.is_approved === undefined || mergedPayload.is_approved === null) {
          mergedPayload.is_approved = rowData.is_approved;
        }

        return mergedPayload;
      },
    });
  },
  handleSaleApproveFee(row, nextApproved = null) {
    this.approveFeeTarget = row && typeof row === 'object' ? { ...row } : null;
    this.approveFeeTargetState = resolveFeeApprovalTargetState(row, nextApproved);
    this.isApproveFeeModalOpen = true;
  },
  handleSaleDeleteFee(row) {
    this.deleteFeeTarget = row && typeof row === 'object' ? { ...row } : null;
    this.isDeleteFeeModalOpen = true;
  },
  handleSaleDownloadFeeXml(row) {
    notifyFeeActionPending(this, row, { actionLabel: FEES_FLOW_TEXT.pending.actions.downloadXml });
  },
  handleSaleDownloadFeePdf(row) {
    notifyFeeActionPending(this, row, { actionLabel: FEES_FLOW_TEXT.pending.actions.downloadPdf });
  },
  handleSaleDownloadFeeReceipt(row) {
    notifyFeeActionPending(this, row, { actionLabel: FEES_FLOW_TEXT.pending.actions.downloadReceipt });
  },
  openFeeModal(mode = 'create', initialData = null) {
    openFeeModalState(this, mode, initialData);
  },
  closeSaleApproveFeeModal() {
    if (this.isApprovingFee) {
      return;
    }

    this.isApproveFeeModalOpen = false;
    this.approveFeeTarget = null;
    this.approveFeeTargetState = true;
  },
  closeSalePayFeeModal() {
    if (this.isPayFeeSubmitting) {
      return;
    }

    this.isPayFeeModalOpen = false;
    this.isPayFeeSubmitting = false;
    this.payFeeTarget = null;
  },
  closeSaleDeleteFeeModal() {
    if (this.isDeletingFee) {
      return;
    }

    this.isDeleteFeeModalOpen = false;
    this.deleteFeeTarget = null;
  },
  closeFeeModal() {
    closeFeeModalState(this);
  },
  async submitSalePayFee(payload = {}) {
    await submitFeePayFlow(this, payload, {
      onSuccess: async () => {
        await this.fetchSaleDetail();
      },
    });
  },
  async confirmSaleApproveFee() {
    await confirmFeeApproveFlow(this, {
      onSuccess: async () => {
        await this.fetchSaleDetail();
      },
    });
  },
  async confirmSaleDeleteFee() {
    await confirmFeeDeleteFlow(this, {
      onSuccess: async () => {
        await this.fetchSaleDetail();
      },
    });
  },
  async submitSaleFee(payload) {
    await submitFeeFlow(this, payload, {
      mapRequestData: (requestData) => {
        if (this.saleId > 0) {
          if (requestData instanceof FormData) {
            requestData.set('sale_id', String(this.saleId));
          } else {
            requestData.sale_id = this.saleId;
          }
        }
      },
      onSuccess: async () => {
        await this.fetchSaleDetail();
      },
    });
  },
  handleSaleClearFeesFilters() {
    this.isClearSaleFeesConfirmModalOpen = true;
  },
  closeClearSaleFeesConfirmModal() {
    if (this.isClearingSaleFees) {
      return;
    }

    this.isClearSaleFeesConfirmModalOpen = false;
  },
  async confirmClearSaleFees() {
    if (!this.saleId || this.isClearingSaleFees) {
      this.closeClearSaleFeesConfirmModal();
      return;
    }

    this.isClearingSaleFees = true;
    try {
      const response = await feesService.clearBySale(this.saleId);
      const apiErrorMessage = extractApiErrorMessage(response, 'No fue posible limpiar comisiones del negocio.');
      if (apiErrorMessage) {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, 'La limpieza de comisiones del negocio se completó correctamente.'));
      this.isClearSaleFeesConfirmModalOpen = false;
      await this.fetchSaleDetail();
    } catch (error) {
      this.$toast?.error(error?.response?.data?.message || error?.message || 'No fue posible limpiar comisiones del negocio.');
    } finally {
      this.isClearingSaleFees = false;
    }
  },
  handleSaleNewFeeFromFees() {
    this.openFeeModal();
  },
};
